import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { toScreen } from './PointerControls';

/** Mouse moves are sent at most this often (ms); presses, releases and keys go right away. */
const MOVE_EVERY = 33;

/**
 * Interact (#306): the layer over the child's screen that sends the parent's mouse, wheel and keyboard to their
 * computer. Clicking the picture focuses it, so keys go to the child's computer until the parent clicks elsewhere.
 */
export function ControlLayer({ videoRef, send }: { videoRef: RefObject<HTMLVideoElement | null>; send: (m: object) => void }) {
  const layerRef = useRef<HTMLDivElement>(null);
  const last = useRef(0);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  const at = (clientX: number, clientY: number) => {
    const video = videoRef.current;
    return video ? toScreen(video, clientX, clientY) : null;
  };

  // The wheel needs a non-passive listener so the page doesn't scroll instead.
  useEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const p = at(e.clientX, e.clientY);
      if (!p) return;
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 400 : 1; // lines / pages -> pixels
      send({ kind: 'wheel', ...p, dx: e.deltaX * unit, dy: e.deltaY * unit });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [send]);

  useEffect(() => {
    layerRef.current?.focus();
    return () => {
      if (pending.current) clearTimeout(pending.current);
    };
  }, []);

  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = at(e.clientX, e.clientY);
    if (!p) return;
    const go = () => {
      last.current = Date.now();
      pending.current = null;
      send({ kind: 'move', ...p });
    };
    if (pending.current) clearTimeout(pending.current);
    const wait = MOVE_EVERY - (Date.now() - last.current);
    if (wait <= 0) go();
    else pending.current = setTimeout(go, wait); // the last position always gets there
  };

  const press = (e: ReactPointerEvent<HTMLDivElement>, kind: 'down' | 'up') => {
    const p = at(e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    if (kind === 'down') {
      e.currentTarget.focus();
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (pending.current) clearTimeout(pending.current);
    pending.current = null;
    send({ kind: 'move', ...p });
    send({ kind, ...p, button: e.button === 1 || e.button === 2 ? e.button : 0 });
  };

  const key = (e: ReactKeyboardEvent<HTMLDivElement>, kind: 'keydown' | 'keyup') => {
    e.preventDefault();
    send({ kind, key: e.key, code: e.code, ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey });
  };

  return (
    <div
      ref={layerRef}
      className="pointer-layer control-layer"
      tabIndex={0}
      onPointerMove={move}
      onPointerDown={(e) => press(e, 'down')}
      onPointerUp={(e) => press(e, 'up')}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => key(e, 'keydown')}
      onKeyUp={(e) => key(e, 'keyup')}
      aria-label="Control their computer: click, scroll and type here"
    />
  );
}
